import React, {ComponentType, useCallback, useEffect, useMemo} from "react"
import {Field, FieldProps, Formik, FormikProps} from "formik"
import {useToasts} from "react-toast-notifications"

import {useAccessToken} from "../../services/auth"
import {JWTProviders} from "../../resources/orgSettings"
import Auth0Fields from "../../components/JwtProviders/managedSolutions/Auth0Fields"
import StytchFileds from "../../components/JwtProviders/managedSolutions/StytchFileds"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import CognitoFields from "../../components/JwtProviders/managedSolutions/CognitoFields"
import OktaFields from "../../components/JwtProviders/managedSolutions/OktaFields"
import {AppConfig, updateAppConfig} from "../../resources/whiteLabel"
import CustomFields from "../../components/JwtProviders/managedSolutions/CustomFields"
import CustomWithJWKSFields from "../../components/JwtProviders/managedSolutions/CustomWithJWKSFields"
import Button from "../../components/Button/Button"
import {AuthSettingsFormValues} from "./types"

const providerFieldsMap: Record<JWTProviders, ComponentType> = {
	[JWTProviders.Auth0]: Auth0Fields,
	[JWTProviders.Stytch]: StytchFileds,
	[JWTProviders.Cognito]: CognitoFields,
	[JWTProviders.Okta]: OktaFields,
	[JWTProviders.Custom]: CustomFields,
	[JWTProviders.CustomWithJWKS]: CustomWithJWKSFields,
}

export default function Authentication({appConfig, refresh}: {appConfig: AppConfig; refresh: () => void}) {
	const accessToken = useAccessToken()
	const [state, patch] = useAsyncResultIdle(updateAppConfig)
	const {addToast} = useToasts()

	useEffect(() => {
		if (state.isOk()) {
			addToast("Org Settings Updated Successfully", {appearance: "success"})
			refresh()
		}
	}, [state])

	const initialValues = useMemo<AuthSettingsFormValues>(() => {
		return {
			provider: appConfig?.attributes?.jwtProvider?.provider ?? JWTProviders.Custom,
			jwksPath: appConfig?.attributes?.jwtProvider?.jwksPath ?? "",
			jwtIssuer: appConfig?.attributes?.jwtIssuer ?? undefined,
			jwtPublicKey: appConfig?.attributes?.jwtPublicKey ?? "",
			audience: appConfig?.attributes?.jwtProvider?.audience ?? undefined,
		}
	}, [appConfig])

	const handleSubmit = useCallback(
		(values: AuthSettingsFormValues) => {
			patch(accessToken, {
				jwtProvider: {
					provider: values.provider === JWTProviders.Custom ? null : values.provider,
					jwksPath: values.jwksPath === "" ? null : values.jwksPath,
					audience: values.audience === "" ? null : values.audience,
				},
				jwtIssuer: values.jwtIssuer,
				jwtPublicKey: values.provider === JWTProviders.Custom ? values.jwtPublicKey : null,
			})
		},
		[patch, accessToken]
	)

	return (
		<div className="card managed-solutions-settings-card">
			<div className={"authentication-content card-content"}>
				<div className="description">
					<p>
						We support tokens from Okta, Auth0, AWS Cognito, and Stytch—just configure the JWKS Path and JWT Issuer.
						Using a different provider with a JWKS endpoint? Choose Custom with JWKS for any provider that publishes
						their keys via JWKS. Need to validate tokens with a public key directly? Choose Custom and provide the
						public key for validation.
					</p>
				</div>
				<Formik initialValues={initialValues} onSubmit={handleSubmit}>
					{({handleSubmit, values: {provider}, resetForm}: FormikProps<AuthSettingsFormValues>) => {
						const Fields = providerFieldsMap[provider]

						const handleProviderChange = useCallback(
							(event: React.ChangeEvent<HTMLSelectElement>) => {
								const newProvider = event.target.value as JWTProviders
								resetForm({
									values: {
										provider: newProvider,
										jwksPath: "",
										jwtIssuer: "",
										jwtPublicKey: "",
										audience: undefined,
									},
								})
							},
							[resetForm]
						)

						return (
							<form onSubmit={handleSubmit}>
								<label className="label" htmlFor="jwt-provider">
									Identity Provider
								</label>
								<div className="select">
									<Field name="provider">
										{({field: {value, name}}: FieldProps<JWTProviders>) => (
											<select id="jwt-provider" name={name} value={value} onChange={handleProviderChange}>
												<option value={JWTProviders.Auth0}>Auth0</option>
												<option value={JWTProviders.Stytch}>Stytch</option>
												<option value={JWTProviders.Cognito}>Amazon Cognito</option>
												<option value={JWTProviders.Okta}>Okta</option>
												<option value={JWTProviders.Custom}>Custom</option>
												<option value={JWTProviders.CustomWithJWKS}>Custom with JWKS</option>
											</select>
										)}
									</Field>
								</div>

								{Fields && <Fields />}

								<div className="columns">
									<div className="column mt-4 button-container">
										{state.match(
											() => (
												<Button className="button primary" type="submit" prefixIcon="approve-v-check-validation">
													Save
												</Button>
											),
											() => (
												<Button className="button primary is-loading">Saving</Button>
											),
											(_) => null,
											(err) => (
												<>
													<div className="has-text-danger">{err.errors[0].title}</div>
													<Button className="button primary" type="submit" prefixIcon="approve-v-check-validation">
														Save
													</Button>
												</>
											)
										)}
									</div>
								</div>
							</form>
						)
					}}
				</Formik>
			</div>
		</div>
	)
}
